import { Button, Input, Otp } from "@cynthai/ui";
import { createFileRoute, redirect, useNavigate } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";

import { isApiError } from "@/core/lib/api-errors";
import { RecoveryCodes } from "@/features/auth/components/RecoveryCodes";
import { useTwoFaRecover } from "@/features/auth/hooks/use-two-fa-recover";
import { useTwoFaVerifySignIn } from "@/features/auth/hooks/use-two-fa-verify-signin";
import { m } from "@/paraglide/messages";

function TwoFactorAuth() {
  const otpRef = useRef<HTMLInputElement | null>(null);
  const recoveryRef = useRef<HTMLInputElement | null>(null);
  const [method, setMethod] = useState<"totp" | "recovery">("totp");
  const [recoveryCode, setRecoveryCode] = useState("");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const navigate = useNavigate();

  function handleError(error: unknown) {
    if (isApiError(error, "INVALID_OTP")) {
      setErrorMessage(m.two_fa_invalid_otp());
    } else if (isApiError(error, "INVALID_RECOVERY_CODE")) {
      setErrorMessage(m.two_fa_invalid_recovery_code());
    } else if (isApiError(error, "TWO_FA_NOT_GENERATED")) {
      navigate({ to: "/auth/two-fa" });
    } else if (!isApiError(error, "TOO_MANY_ATTEMPTS")) {
      setErrorMessage(m.two_fa_unknown_server_error());
    }
  }

  const verify = useTwoFaVerifySignIn({ onError: handleError });
  const recover = useTwoFaRecover({ onError: handleError });
  const isPending = verify.isPending || recover.isPending;
  const recoveryCodes = verify.recoveryCodes ?? recover.recoveryCodes;
  const continueAfterSaving = verify.recoveryCodes
    ? verify.continueAfterSaving
    : recover.continueAfterSaving;

  useEffect(() => {
    if (errorMessage && !isPending) {
      const input = method === "recovery" ? recoveryRef : otpRef;
      input.current?.focus();
    }
  }, [errorMessage, isPending, method]);

  function verifyOtp(value?: string) {
    if (value?.length === 6 && !isPending && !recoveryCodes) {
      setErrorMessage(null);
      verify.mutate({ body: { otp: value } });
    }
  }

  if (recoveryCodes) {
    return (
      <RecoveryCodes codes={recoveryCodes} onContinue={continueAfterSaving} />
    );
  }

  return (
    <>
      <h1 className="text-text-primary mb-2 text-2xl font-semibold">
        {m.two_fa_signin_title()}
      </h1>
      <p className="text-text-secondary mb-9">
        {method === "recovery"
          ? m.two_fa_recovery_instructions()
          : m.two_fa_signin_instructions()}
      </p>
      <form
        onSubmit={(event) => {
          event.preventDefault();
          if (isPending) return;
          if (method === "recovery") {
            if (!recoveryCode.trim()) return;
            setErrorMessage(null);
            recover.mutate({ body: { code: recoveryCode } });
          } else {
            verifyOtp(otpRef.current?.value);
          }
        }}
      >
        {method === "recovery" ? (
          <Input
            ref={recoveryRef}
            autoFocus
            label={m.two_fa_recovery_label()}
            autoComplete="off"
            autoCapitalize="characters"
            spellCheck={false}
            value={recoveryCode}
            disabled={isPending}
            aria-invalid={errorMessage !== null}
            isInvalid={errorMessage !== null}
            errorMessage={errorMessage ?? undefined}
            onChange={(event) => {
              setRecoveryCode(event.target.value);
              setErrorMessage(null);
            }}
          />
        ) : (
          <Otp
            ref={otpRef}
            autoFocus
            aria-label={m.two_fa_otp_label()}
            onComplete={verifyOtp}
            disabled={isPending}
            errorMessage={errorMessage}
            isInvalid={errorMessage !== null}
            onChange={() => setErrorMessage(null)}
          />
        )}
        <div className="my-2 mt-10 flex flex-col items-stretch gap-4">
          <Button
            type="submit"
            color="primary"
            size="lg"
            isLoading={isPending}
            isDisabled={method === "recovery" && !recoveryCode.trim()}
          >
            {m.two_fa_verify_button_label()}
          </Button>
          <Button
            variant="ghost"
            isDisabled={isPending}
            onPress={() => {
              setMethod(method === "totp" ? "recovery" : "totp");
              setRecoveryCode("");
              setErrorMessage(null);
              verify.reset();
              recover.reset();
            }}
          >
            {method === "totp"
              ? m.two_fa_use_recovery_code()
              : m.two_fa_use_authenticator()}
          </Button>
        </div>
      </form>
    </>
  );
}

export const Route = createFileRoute("/auth/two-fa-verify")({
  component: () => <TwoFactorAuth />,
  beforeLoad: ({ context }) => {
    if (context.user) {
      throw redirect({ to: "/" });
    }
  },
});
